
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface OrderProduct {
  sku: string;
  ean: string | null;
  productTitle: string;
  variantTitle: string | null;
  quantity: number;
  color: string | null;
  imageUrl: string | null;
  brandTitle: string | null;
  artikelCode: string | null;
  productId: string | null;
  supplierTitle?: string | null;
  eenheid?: string | null;
  voorraadProductName?: string | null;
}

/**
 * Standalone fetch function for order products by VO number.
 * Reusable outside of the React hook (e.g. for bulk fetching).
 */
export const fetchOrderProducts = async (voNumber: string): Promise<OrderProduct[]> => {
  const trimmed = voNumber.trim();
  if (!trimmed) return [];

  const normalized = trimmed.toUpperCase().startsWith('VO') ? trimmed.toUpperCase() : `VO${trimmed}`;

  let { data, error: fetchError } = await supabase
    .from("orders_live_ls")
    .select("sku, ean, producttitle, varianttitle, quantityordered, customfieldcolor, image_url, brand_title, artikel_code, product_id, voornaam_klant, suppliertitle")
    .eq("ordernumber", normalized);

  if (!fetchError && (!data || data.length === 0) && normalized !== trimmed) {
    const fallback = await supabase
      .from("orders_live_ls")
      .select("sku, ean, producttitle, varianttitle, quantityordered, customfieldcolor, image_url, brand_title, artikel_code, product_id, voornaam_klant, suppliertitle")
      .eq("ordernumber", trimmed);
    if (!fallback.error) {
      data = fallback.data;
      fetchError = fallback.error;
    }
  }

  if (fetchError) throw fetchError;
  if (!data || data.length === 0) return [];

  // Deduplicate by SKU
  const skuMap = new Map<string, typeof data[0]>();
  for (const row of data) {
    const key = row.sku || row.producttitle || "";
    const existing = skuMap.get(key);
    const isBetter = !existing
      || (row.image_url && !existing.image_url)
      || (row.product_id && !existing.product_id && !!row.image_url === !!existing.image_url)
      || (row.voornaam_klant && !existing.voornaam_klant && !!row.image_url === !!existing.image_url);
    if (isBetter) skuMap.set(key, row);
  }

  // Fallback images
  const missingImageSkus = Array.from(skuMap.values())
    .filter(row => !row.image_url && row.sku)
    .map(row => row.sku) as string[];

  if (missingImageSkus.length > 0) {
    const fallbackResults = await Promise.allSettled(
      missingImageSkus.map(sku =>
        supabase
          .from("orders_live_ls")
          .select("sku, image_url")
          .eq("sku", sku)
          .not("image_url", "is", null)
          .limit(1)
      )
    );

    const fallbackMap = new Map<string, string>();
    for (const result of fallbackResults) {
      if (result.status === "fulfilled" && result.value.data) {
        for (const row of result.value.data) {
          if (row.image_url && row.sku && !fallbackMap.has(row.sku)) {
            fallbackMap.set(row.sku, row.image_url);
          }
        }
      }
    }
    for (const [key, row] of skuMap.entries()) {
      if (!row.image_url && row.sku && fallbackMap.has(row.sku)) {
        skuMap.set(key, { ...row, image_url: fallbackMap.get(row.sku)! });
      }
    }
  }

  // Fetch eenheid from voorraad_live (join on sku, not product_id)
  const skus = Array.from(skuMap.values())
    .map(row => row.sku)
    .filter(Boolean) as string[];

  const voorraadMap = new Map<string, { eenheid: string; product_naam: string }>();
  if (skus.length > 0) {
    // Step 1: exact match
    const { data: voorraadData } = await supabase
      .from("voorraad_live")
      .select("product_id, eenheid, product_naam")
      .in("product_id", skus);
    if (voorraadData) {
      for (const v of voorraadData) {
        if (v.product_id) {
          voorraadMap.set(v.product_id, {
            eenheid: v.eenheid || "",
            product_naam: v.product_naam || "",
          });
        }
      }
    }

    // Step 2: fuzzy fallback for unmatched SKUs (e.g. KX variant → WN/ZN variant)
    const unmatchedSkus = skus.filter(s => !voorraadMap.has(s) && s.length >= 15);
    if (unmatchedSkus.length > 0) {
      const fuzzyResults = await Promise.allSettled(
        unmatchedSkus.map(sku => {
          const basePrefix = sku.substring(0, 10);
          const volumeSuffix = sku.substring(sku.length - 5);
          return supabase
            .from("voorraad_live")
            .select("product_id, eenheid, product_naam")
            .like("product_id", `${basePrefix}%${volumeSuffix}`)
            .not("eenheid", "is", null)
            .limit(1);
        })
      );
      for (let i = 0; i < fuzzyResults.length; i++) {
        const result = fuzzyResults[i];
        if (result.status === "fulfilled" && result.value.data && result.value.data.length > 0) {
          const row = result.value.data[0];
          voorraadMap.set(unmatchedSkus[i], {
            eenheid: row.eenheid || "",
            product_naam: row.product_naam || "",
          });
        }
      }
    }
  }

  return Array.from(skuMap.values()).map(row => {
    const voorraad = row.sku ? voorraadMap.get(row.sku) : undefined;
    return {
      sku: row.sku || "",
      ean: row.ean,
      productTitle: row.producttitle || voorraad?.product_naam || "",
      voorraadProductName: voorraad?.product_naam || null,
      variantTitle: row.varianttitle,
      quantity: row.quantityordered || 1,
      color: row.customfieldcolor,
      imageUrl: row.image_url,
      brandTitle: row.brand_title,
      artikelCode: row.artikel_code,
      productId: row.product_id,
      supplierTitle: row.suppliertitle,
      eenheid: voorraad?.eenheid || null,
    };
  });
};

export const useOrderProducts = (voNumber: string) => {
  const [products, setProducts] = useState<OrderProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const trimmed = voNumber.trim();
    if (!trimmed) {
      setProducts([]);
      setError(null);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await fetchOrderProducts(trimmed);
        setProducts(result);
      } catch (err: any) {
        console.error("Error fetching order products:", err);
        setError("Kon producten niet ophalen");
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [voNumber]);

  return { products, isLoading, error };
};
